/**
 * cart-sync.js
 * Keeps the theme cart UI in sync with cart changes made by third-party app
 * scripts (gift-with-purchase, upsells, bundles) that call the Cart AJAX API
 * directly without notifying the theme.
 *
 * How it works: window.fetch and XMLHttpRequest are wrapped. When a request
 * that mutates the cart (/cart/add, /cart/change, /cart/update, /cart/clear)
 * completes successfully, a debounced `cart:refresh` event is dispatched.
 * The theme's cart components listen for it and re-render.
 *
 * Double-render protection: theme components record window.__themeCartSyncedAt
 * whenever they re-render the cart UI themselves. If a render already happened
 * after the mutation completed, the scheduled refresh is skipped, so normal
 * theme adds/quantity changes never render twice (no flicker).
 */
(function () {
  'use strict';

  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var timer = null;
  var lastMutationAt = 0;

  /**
   * Processing lock: while any cart mutation is in flight, the cart UI
   * (drawer + cart page) gets the `cart-processing` class on <html>, which
   * disables pointer events on it. Prevents double-clicks on +/-, remove and
   * checkout from firing overlapping cart requests. Unlocks 200ms after the
   * last pending mutation settles (covers the DOM re-render window), with a
   * 10s safety timeout so the cart can never stay locked.
   */
  var pending = 0;
  var unlockTimer = null;
  var safetyTimer = null;

  function lockCart() {
    pending++;
    clearTimeout(unlockTimer);
    document.documentElement.classList.add('cart-processing');
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(function () {
      pending = 0;
      document.documentElement.classList.remove('cart-processing');
    }, 10000);
  }

  function releaseCart() {
    pending = Math.max(0, pending - 1);
    if (pending === 0) {
      clearTimeout(unlockTimer);
      unlockTimer = setTimeout(function () {
        if (pending === 0) document.documentElement.classList.remove('cart-processing');
      }, 200);
    }
  }

  function scheduleRefresh() {
    lastMutationAt = Date.now();
    clearTimeout(timer);
    timer = setTimeout(function () {
      if ((window.__themeCartSyncedAt || 0) >= lastMutationAt) return; // theme already rendered this change
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'cart-sync' } }));
    }, 300);
  }

  // --- fetch ---
  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var isMutation = MUTATION_RE.test(url);
    if (isMutation) lockCart();
    var promise = originalFetch.apply(this, arguments);
    if (isMutation) {
      promise.then(
        function (response) {
          if (response && response.ok) scheduleRefresh();
          releaseCart();
        },
        function () {
          releaseCart();
        }
      );
    }
    return promise;
  };

  // --- XMLHttpRequest (older app scripts) ---
  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCartMutation = MUTATION_RE.test(String(url));
    return originalOpen.apply(this, arguments);
  };
  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__isCartMutation) {
      lockCart();
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleRefresh();
      });
      this.addEventListener('loadend', function () {
        releaseCart();
      });
    }
    return originalSend.apply(this, arguments);
  };
})();
